"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function KnowledgeConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div data-index={i} key={i} className="border border-border bg-card p-3 pb-7">
            <div className="aspect-[16/10] w-full bg-muted/40 animate-pulse" />
            <div className="px-4 pt-6 space-y-3">
              <div className="h-3 w-32 bg-muted/40 animate-pulse rounded" />
              <div className="h-6 w-full bg-muted/40 animate-pulse rounded" />
              <div className="h-6 w-3/4 bg-muted/40 animate-pulse rounded" />
              <div className="h-4 w-full bg-muted/30 animate-pulse rounded mt-3" />
              <div className="h-4 w-5/6 bg-muted/30 animate-pulse rounded" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-10 text-center">
        <p className="text-sm text-muted-foreground">
          {"We couldn't load the latest landscaping articles."}
        </p>
        <Button variant="outline" onClick={refetch} className="mt-4 border-border text-foreground hover:bg-accent">
          Try again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="border border-border bg-card p-10 text-center">
        <p className="text-sm text-muted-foreground">
          New landscaping tips are on the way. Check back soon.
        </p>
      </div>;
  }
  const categoryLabels = ["DESIGN", "MAINTENANCE", "MATERIALS"];
  const readTimes = ["5 min read", "3 min read", "7 min read"];
  return <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => {
      const category = post.categories && post.categories[0]?.name?.toUpperCase() || categoryLabels[i % categoryLabels.length];
      const readTime = readTimes[i % readTimes.length];
      const dateStr = post.date ? new Date(post.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : "";
      const authorName = post.author?.name || "London Stone Team";
      const avatarUrl = post.author?.avatar?.["48"] || post.author?.avatar?.["96"] || "";
      return <Link data-index={i} key={post.id} to={post.link || "#"} className="group block">
            <article className="h-full border border-border bg-card pb-7 transition-all duration-300 hover:-translate-y-[3px] hover:shadow-lg">
              <div className="p-3">
                <div className="relative aspect-[16/10] w-full overflow-hidden border border-border">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[320ms] ease-out group-hover:scale-[1.03]" /> : <div className="h-full w-full bg-muted/30" />}
                </div>
              </div>
              <div className="px-7 pt-5">
                <div className="flex items-center gap-2 font-mono text-[0.75rem] uppercase tracking-wide text-muted-foreground">
                  <span>{category}</span>
                  <span aria-hidden="true">{"\u00b7"}</span>
                  <span>{readTime}</span>
                </div>
                <h3 className="font-default mt-3 text-xl md:text-[1.625rem] font-semibold leading-[1.15] tracking-tight text-foreground transition-colors duration-200 group-hover:text-primary line-clamp-2" style={{
              letterSpacing: "-0.018em"
            }}>
                  {post.title}
                </h3>
                {post.excerpt && <p className="mt-3 text-[0.9375rem] leading-[1.6] text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                <div className="mt-6 flex items-center gap-3">
                  {avatarUrl ? <img src={avatarUrl} alt={authorName} loading="lazy" className="h-7 w-7 rounded-lg border border-border object-cover" /> : <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-muted text-[0.625rem] font-medium text-muted-foreground">
                      {authorName.charAt(0)}
                    </div>}
                  <span className="text-[0.8125rem] font-medium text-foreground">
                    {authorName}
                  </span>
                  {dateStr && <>
                      <span className="text-muted-foreground/60" aria-hidden="true">
                        {"\u00b7"}
                      </span>
                      <span className="font-mono text-[0.8125rem] text-muted-foreground">
                        {dateStr}
                      </span>
                    </>}
                </div>
              </div>
            </article>
          </Link>;
    })}
    </div>;
}
export default function Insights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="6536" id="featuredpost" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2rem, 4.2vw, 3.5rem)",
            fontWeight: 540,
            letterSpacing: "-0.028em",
            lineHeight: 1.04
          }}>
              Expert <em className="font-medium italic">landscaping</em> advice.
            </h2>
          </div>
          <div className="flex flex-col justify-end lg:col-span-4">
            <p className="text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>
              Discover professional tips on stone selection, garden design, and long-term maintenance to elevate your outdoor living space.
            </p>
            <Link to="/BlogPage" className="mt-5 inline-flex w-fit items-center gap-1.5 text-[0.9375rem] font-medium text-foreground underline decoration-primary decoration-[1.5px] underline-offset-[6px] transition-all duration-200 hover:decoration-2">
              View all insights
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="mt-14">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date"
        }}>
            <KnowledgeConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}